import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { MdNotifications } from "react-icons/md";
import useAlertCount from "../../utils/useAlertCount";
import PaymentActivation from "../pages/PaymentActivation";
import { API_BASE_URL } from "../../config/api";
import { authHeader, handleApiResponse } from "../../utils/authHeader";
import "../../styles/owner.css";

const OwnerLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subStatus,   setSubStatus]   = useState(null);
  const [searchAllowance, setSearchAllowance] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const canReceiveAlerts = planFeatures?.canReceiveAlerts !== false;
  const { unreadCount } = useAlertCount(canReceiveAlerts);
  const navigate  = useNavigate();
  const location  = useLocation();
  const onSubPage = location.pathname === "/owner/subscription";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/subscription/my`, {
      headers: authHeader(),
    })
      .then(res => {
        if (!handleApiResponse(res)) return;
        return res.json();
      })
      .then(data => {
        const status = data?.subscription?.status || "trial";
        setSubStatus(status);
        setSearchAllowance(data?.searchAllowance || { limit: 0 });
        setPlanFeatures(data?.planFeatures || {});
        // Cache so dashboard can read without extra API call
        localStorage.setItem("subStatus", status);
      })
      .catch(() => {});
  }, []);

  // Only block access when subscription has fully expired (must renew)
  if (subStatus === "expired") {
    return <PaymentActivation />;
  }

  return (
    <div className="owner-wrapper">

      {/* ── Hamburger button (mobile only) ── */}
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open menu"
      >
        ☰
      </button>

      {/* ── Bell icon — top right (mobile) ── */}
      {canReceiveAlerts && <button
        className="notif-bell-mobile"
        onClick={() => navigate("/owner/notifications")}
        aria-label="Notifications"
        style={{
          position: "fixed", top: "14px", right: "16px",
          zIndex: 1100, background: "none", border: "none",
          cursor: "pointer", display: "none",
        }}
      >
        <div style={{ position: "relative", display: "inline-flex" }}>
          <MdNotifications style={{ fontSize: "26px", color: "#1b2559" }} />
          {unreadCount > 0 && (
            <span style={{
              position: "absolute", top: "-4px", right: "-4px",
              background: "#ef4444", color: "#fff",
              fontSize: "9px", fontWeight: "700",
              borderRadius: "10px", padding: "1px 5px",
              minWidth: "16px", textAlign: "center",
            }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </div>
      </button>}

      {/* ── Overlay (mobile only) ── */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ── */}
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} planFeatures={planFeatures || {}} />
      </div>

      {/* ── Main content ── */}
      <div className="owner-main">
        <div className="owner-content">

          {/* ── Upgrade banner for trial users (hidden on subscription page itself) ── */}
          {subStatus === "trial" && !onSubPage && (
            <div style={{
              background: "linear-gradient(135deg, #4318FF, #7551FF)",
              borderRadius: 14, padding: "12px 18px", marginBottom: 16,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>🔒</span>
                <div>
                  <div style={{ fontWeight: 700, color: "#fff", fontSize: 13 }}>
                    You're on Free Trial — {searchAllowance?.limit === -1 ? "unlimited searches" : searchAllowance?.limit > 0 ? `${searchAllowance.limit} searches per day` : "search is unavailable"}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginTop: 1 }}>
                    Upgrade for more searches, alerts & exports
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate("/owner/subscription")}
                style={{
                  padding: "8px 18px", background: "#fff", color: "#4318FF",
                  border: "none", borderRadius: 8, fontWeight: 800, fontSize: 13,
                  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                }}>
                Upgrade Now →
              </button>
            </div>
          )}

          {/* ── Pending banner ── */}
          {subStatus === "pending" && !onSubPage && (
            <div style={{
              background: "#fffbeb", border: "1.5px solid #fde68a",
              borderRadius: 14, padding: "12px 18px", marginBottom: 16,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>⏳</span>
              <div style={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
                Payment under review — account will be activated within 12 hours.
              </div>
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default OwnerLayout;